import { price } from '@eti/utils';
import { Overlay } from '@eti/components';
import { css } from '@eti/styles';
import { breakpoints, Card, Inline, Stack, vars } from '@etg/wings';
import { useDateFormat, useDirection, useTranslation } from '@eti/providers';
import { useMediaQuery } from 'usehooks-ts';
import {
  formatGenesysRebookingTrip,
  getTripDisplayRebookingPrice,
  groupTravelersByAgeType,
} from '../utils';
import { addLegendNumberToSegmentEvents } from '../../../widgets/trip-details/utils/format';
import TripDetailsBounds from '../../result/components/v3/resultTripCards/components/TripDetailsBounds';
import { TripBaggageAndPriceInfo } from '../../result/components/v3/resultTripCards/components/TripBaggageAndPriceInfo';
import { usePriceDisplayType } from '../../result/utils/resultUtils';
import { useToggle } from '../../../common/custom-hooks/toggle';
import type { RebookingTripType } from '../models';
import type { RebookEligibilityType } from '../../order-details/components/manage-trip/rebooking/models';
import { useChat } from '../../../common/chat/ChatContext';
import RebookingTripDrawerContent from './RebookingTripDrawerContent';

const cardStyles = css`
  cursor: pointer;

  @media (min-width: ${breakpoints._768}) {
    cursor: default;
  }
`;

const boundWrapperStyles = (isRTL: boolean) => css`
  flex: 1;
  justify-content: center;
  position: relative;

  ::after {
    border-right: 1px solid ${vars.colors.divider};
    content: '';
    height: calc(100% + 16px);
    left: ${isRTL ? 0 : ''};
    position: absolute;
    right: ${isRTL ? '' : 0};
    top: -8px;
    width: 1px;

    @media (max-width: ${breakpoints._768}) {
      border-right: none;
    }
  }
`;

interface RebookingTripResultProps {
  flight: RebookingTripType;
  hasAirlineAncillaries: boolean;
  index: number;
  rebookEligibilityType: RebookEligibilityType;
}

const RebookingTripResult = ({
  flight,
  rebookEligibilityType,
  hasAirlineAncillaries,
  index,
}: RebookingTripResultProps) => {
  const { t } = useTranslation();
  const { priceDisplayText, priceDisplayType } = usePriceDisplayType();
  const { setGenesysExtraInfo } = useChat();
  const { isRTL } = useDirection();
  const { dayTime } = useDateFormat();
  const { handleOpen, handleClose, isOpen } = useToggle(false);
  const isMobileScreen = useMediaQuery(`(max-width: ${breakpoints._768})`);

  const pricesPerGroup = groupTravelersByAgeType(
    flight.travelers,
    flight.rebookingTravelerPricesWithoutPaymentDiscounts,
    rebookEligibilityType,
  );

  const ticket = {
    priceDisplayText,
    pricePerPerson: price.formatPrice(
      getTripDisplayRebookingPrice(priceDisplayType, pricesPerGroup),
    ),
  };

  const boundsWithLegends = addLegendNumberToSegmentEvents(flight.bounds);

  const handleOnClick = () => {
    handleOpen();
    setGenesysExtraInfo({ rebookingTrip: formatGenesysRebookingTrip(flight, dayTime) });
  };

  return (
    <>
      <Card
        className={cardStyles}
        data-testid={`changeTrip-resultTrip-${index}-wrapper`}
        onClick={isMobileScreen ? handleOnClick : undefined}
      >
        <Inline collapseBelow={breakpoints._768} spacing={0}>
          <Stack className={boundWrapperStyles(isRTL)} spacing={0}>
            <TripDetailsBounds
              boundsWithLegends={boundsWithLegends}
              flightId={flight.id}
              type={flight.type}
            />
          </Stack>
          <TripBaggageAndPriceInfo
            bounds={flight.bounds}
            includedCabinBaggage={flight.includedCabinBaggage}
            includedCheckedBaggage={flight.includedCheckedBaggage}
            numberOfTravelers={flight.travelers?.length}
            onClick={handleOnClick}
            ticket={ticket}
          />
        </Inline>
      </Card>
      <Overlay.Drawer
        dataTestId="changeTrip-price-details-drawer"
        handleClose={handleClose}
        isOpen={isOpen}
      >
        <Overlay.Header onClose={handleClose} title={t('ChangeTrip.PriceDetailsDrawer.Header')} />
        <Overlay.Content data-testid="changeTrip-price-details-drawer-content">
          <RebookingTripDrawerContent
            flight={flight}
            hasAirlineAncillaries={hasAirlineAncillaries}
            priceDisplayText={priceDisplayText}
            pricesPerGroup={pricesPerGroup}
            rebookEligibilityType={rebookEligibilityType}
          />
        </Overlay.Content>
      </Overlay.Drawer>
    </>
  );
};

export default RebookingTripResult;
